import type { ReactNode } from 'react';

/**
 * EmptyState — the panel a surface shows when it holds nothing yet.
 *
 * Corpus: `DB:1449-1452` (bold title, secondary description and
 * a ghost action, stacked under a hairline rule), `DB:512-514`
 * (the title-only form the case queue falls back to), and the same panel before
 * the corpus refresh at `DB1:1113-1116`. The corpus title is
 * weight 700 and this panel has always rendered 600; the move keeps 600, so
 * that a directory reshuffle does not quietly restyle every empty surface.
 *
 * Title and body are supplied by the caller. An empty surface is where the
 * wording is most surface-specific — a queue, a case list and an evidence panel
 * are all "empty" for different reasons — and re-typing that copy into a
 * component is the duplication this component removes.
 */
export interface EmptyStateProps {
  readonly title: string;
  readonly children?: ReactNode;
}

export function EmptyState({ title, children }: EmptyStateProps) {
  return (
    <div className="rp-empty">
      {/* 600 is `--rp-weight-semibold`; it was written as a bare `600` here,
          which is the one thing rule 2 of the library contract forbids — a
          central change to the weight would have moved every other title and
          left this one behind. */}
      <p style={{ fontWeight: 'var(--rp-weight-semibold)', margin: 0 }}>{title}</p>
      {children}
    </div>
  );
}
